import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../db/firebase"; // adjust path if needed
import { useAuth } from "../../../context/AuthContext";

const AdminList = () => {
  const { isMainAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({});

  // 🔹 Fetch admins from Firestore
  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const adminsData = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((user) => user.Role === "admin" || user.Role === "main-admin");
      setAdmins(adminsData);
    } catch (err) {
      console.error("Error fetching admins:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // 🔹 Handle form input
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // 🔹 Start editing
  const handleEdit = (admin) => {
    setEditingAdmin(admin.id);
    setFormData({
      firstName: admin.FirstName,
      lastName: admin.LastName,
      role: admin.Role,
      number: admin.Number,
      gender: admin.Gender,
    });
  };

  // 🔹 Cancel edit
  const cancelEdit = () => {
    setEditingAdmin(null);
    setFormData({});
  };

  // 🔹 Update admin in Firestore (except email/password)
  const handleUpdate = async (id) => {
    try {
      const adminRef = doc(db, "users", id);

      await updateDoc(adminRef, {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Role: formData.role,
        Number: formData.number,
        Gender: formData.gender,
      });

      alert("Admin updated successfully!");
      setEditingAdmin(null);
      fetchAdmins();
    } catch (err) {
      console.error("Update failed:", err);
      alert("Failed to update admin. Check console for details.");
    }
  };

  // 🔹 Restrict access
  if (!isMainAdmin) {
    return (
      <div className="text-center mt-20 text-red-600 font-semibold">
        Access denied. Only Main Admins can manage admin accounts.
      </div>
    );
  }

  // 🔹 UI
  return (
    <div className="p-8 bg-offwhite min-h-screen">
      <h1 className="text-3xl font-bold text-primary mb-6">Admin Management</h1>

      {loading ? (
        <p>Loading admins...</p>
      ) : (
        <div className="overflow-x-auto bg-beige rounded-xl shadow-md">
          <table className="min-w-full border border-taupe rounded-xl">
            <thead>
              <tr className="bg-primary text-offwhite">
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Gender</th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-left">Phone</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id} className="border-t border-taupe">
                  {editingAdmin === admin.id ? (
                    <>
                      <td className="p-3">
                        <input
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="border rounded px-2 py-1 w-28"
                        />
                        <input
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className="border rounded px-2 py-1 w-28 ml-2"
                        />
                      </td>
                      <td className="p-3 text-gray-500 italic">
                        {admin.Email}
                      </td>
                      <td className="p-3">
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          className="border rounded px-2 py-1"
                        >
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </td>
                      <td className="p-3">
                        <select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          className="border rounded px-2 py-1"
                        >
                          <option value="admin">Admin</option>
                          <option value="main-admin">Main Admin</option>
                        </select>
                      </td>
                      <td className="p-3">
                        <input
                          name="number"
                          value={formData.number}
                          onChange={handleChange}
                          className="border rounded px-2 py-1 w-32"
                        />
                      </td>
                      <td className="p-3 space-x-2">
                        <button
                          onClick={() => handleUpdate(admin.id)}
                          className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-3">
                        {admin.FirstName} {admin.LastName}
                      </td>
                      <td className="p-3">{admin.Email}</td>
                      <td className="p-3">{admin.Gender}</td>
                      <td className="p-3 capitalize">{admin.Role}</td>
                      <td className="p-3">{admin.Number}</td>
                      <td className="p-3 space-x-2">
                        <button
                          onClick={() => handleEdit(admin)}
                          className="bg-primary text-white px-3 py-1 rounded hover:bg-darktaupe"
                        >
                          Edit
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminList;
